import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthApi from '../../api/platform/authApi';
import StaticDataApi from '../../api/platform/staticDataApi';
import clinicalTrialsLogo from '../../assets/images/clinical-trials-gov-logo-white.png';
import fdaLogo from '../../assets/images/fda-logo-transparent.png';
import logo from '../../assets/images/logo-transparent.png';
import usptoLogo from '../../assets/images/uspto-logo-white.png';
import { useSystemError } from '../../components/Error/SystemErrorContext';
import styles from '../../css/auth/register.module.css';
import { AccountModel } from '../../models/platform/accountModel';
import { CompanySizesModel } from '../../models/platform/companySizesModel';
import { UserModel } from '../../models/platform/userModel';
import { ResponseErrorModel } from '../../models/system/responseErrorModel';
import { ResponseSuccessModel } from '../../models/system/responseSuccessModel';
import { setCookie } from '../../utils/cookiesUtils';
import { handleError, mapResponseErrorToSystemError, mapUnknownErrorToSystemError } from '../../utils/errorUtils';

const Register: React.FC = () => {
  const [user, setUser] = useState<UserModel>({
    email: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    title: '',
    is_verified: false,
  });

  const [account, setAccount] = useState<AccountModel>({
    company_name: '',
    company_size: '',
  });

  const [companySizes, setCompanySizes] = useState<CompanySizesModel[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [didErrorOccur, setDidErrorOccur] = useState(false);
  const { triggerSystemError } = useSystemError();

  const navigate = useNavigate();

  useEffect(() => {
    const fetchCompanySizes = async () => {
      try {
        const response = await StaticDataApi.getCompanySizes();
        if (response.success) {
          const data = (response as ResponseSuccessModel).data;
          setCompanySizes(data);
        } else {
          handleError(mapResponseErrorToSystemError(response as ResponseErrorModel), null, setDidErrorOccur, triggerSystemError);
        }
      } catch (error) {
        handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
      }
    };

    fetchCompanySizes();
  }, []);

  const fetchRegisterUser = async () => {
    try {
      const response = await AuthApi.registerUser(user, account);
      if (response.success) {
        const data = (response as ResponseSuccessModel).data;
        const platform_token = data.platform_token;
        setCookie('platform_token', platform_token);
        navigate('/auth/message/registration-verification');
      } else {
        handleError(mapResponseErrorToSystemError(response as ResponseErrorModel), setSubmissionError, setDidErrorOccur, triggerSystemError);
      }
    } catch (error: unknown) {
      handleError(mapUnknownErrorToSystemError(error, false), setSubmissionError, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleAccountChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setAccount((prevAccount) => ({ ...prevAccount, [name]: value }));
  };

  const handleUserChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUser((prevUser) => ({ ...prevUser, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    setErrors({});
    setSubmissionError(null);

    if (!validateForm()) return;

    await fetchRegisterUser();
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!user.first_name) newErrors.first_name = 'First Name is required';
    if (!user.last_name) newErrors.last_name = 'Last Name is required';
    if (!user.email) newErrors.email = 'Email is required';
    if (!user.title) newErrors.title = 'Title is required';
    if (!user.phone_number) newErrors.phone_number = 'Phone Number is required';
    if (!account.company_name) newErrors.company_name = 'Company Name is required';
    if (!account.company_size) newErrors.company_size = 'Company Size is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return (
    <div className={styles.registerContainer}>
      <div className={styles.leftContainer}>
        <img src={logo} alt="Medical Device IQ" className={styles.logo} />
        <h1>Try Medical Device IQ free</h1>
        <p>
          Test-drive the full Medical Device IQ platform free for 14 days on your choice of AWS, Microsoft Azure, or Google Cloud. Sign-up with your work email to elevate your trial experience.
        </p>
        <ul>
          <li>Create high-quality Generative AI applications...</li>
          <li>Simplify data ingestion and automate ETL...</li>
          <li>Get $400 in serverless compute credits...</li>
        </ul>
        <div className={styles.certifications}>
          <img src={fdaLogo} alt="FDA Certified" />
          <img src={usptoLogo} alt="USPTO Certified" />
          <img src={clinicalTrialsLogo} alt="Clinical Trials" />
        </div>
      </div>
      <div className={styles.rightContainer}>
        <div className={styles.registerCard}>
          <h2>Register</h2>
          <form onSubmit={handleSubmit} noValidate>
            <div className={styles.formGroup}>
              <label htmlFor="first_name">First Name:</label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={user.first_name}
                onChange={handleUserChange}
              />
              {errors.first_name && <div className={styles.errorMessage}>{errors.first_name}</div>}
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="last_name">Last Name:</label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={user.last_name}
                onChange={handleUserChange}
              />
              {errors.last_name && <div className={styles.errorMessage}>{errors.last_name}</div>}
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="email">Email:</label>
              <input
                type="email"
                id="email"
                name="email"
                value={user.email}
                onChange={handleUserChange}
              />
              {errors.email && <div className={styles.errorMessage}>{errors.email}</div>}
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="phone_number">Phone Number:</label>
              <input
                type="text"
                id="phone_number"
                name="phone_number"
                value={user.phone_number}
                onChange={handleUserChange}
              />
              {errors.phone_number && <div className={styles.errorMessage}>{errors.phone_number}</div>}
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="title">Title:</label>
              <input
                type="text"
                id="title"
                name="title"
                value={user.title}
                onChange={handleUserChange}
              />
              {errors.title && <div className={styles.errorMessage}>{errors.title}</div>}
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="company_name">Company Name:</label>
              <input
                type="text"
                id="company_name"
                name="company_name"
                value={account.company_name}
                onChange={handleAccountChange}
              />
              {errors.company_name && <div className={styles.errorMessage}>{errors.company_name}</div>}
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="company_size">Company Size:</label>
              <select
                id="company_size"
                name="company_size"
                value={account.company_size}
                onChange={handleAccountChange}
              >
                <option value="">Select company size</option>
                {companySizes.map((size) => (
                  <option key={size.value} value={size.value}>
                    {size.label}
                  </option>
                ))}
              </select>
              {errors.company_size && <div className={styles.errorMessage}>{errors.company_size}</div>}
            </div>
            <button type="submit" className={styles.submitButton}>Register</button>
            {submissionError && (
              <div className={styles.generalErrorMessage}>
                {submissionError}
              </div>
            )}
          </form>
        </div>
        <div className={styles.loginLink}>
          <p>Already have an account? <Link to="/auth/login">Login here</Link></p>
        </div>
      </div>
    </div>
  );
};

export default Register;